import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaPaperPlane,
  FaSpinner,
  FaRobot,
  FaUser,
  FaCode,
  FaEye,
  FaCopy,
  FaCheck,
  FaFlask,
  FaDownload,
  FaWindowMinimize,
  FaDesktop,
  FaMobileAlt,
} from "react-icons/fa";
import { selectLogInUsername } from "../../common/authenticateDashboard/authSlice";
import { setStudioArtifact, selectStudioArtifact } from "./agentSlice";
import { agentAPI } from "./agentAPI";
import ChatMarkdown from "./components/ChatMarkdown";

// Trigger a download of the variant HTML as a .html file.
export const downloadHtml = (html, name = "variant") => {
  const blob = new Blob([html || ""], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(name || "variant").replace(/[^a-z0-9-_]+/gi, "-")}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeText = (v) => (typeof v === "string" ? v : v == null ? "" : JSON.stringify(v));

// Rotating "what I'm doing" status lines shown while the UI is being built.
const STATUS_STEPS = {
  landing: [
    "Analyzing your current page…",
    "Extracting your brand colors and fonts…",
    "Collecting your real product images…",
    "Reviewing heatmap & user-journey insights…",
    "Designing the new landing page layout…",
    "Writing responsive HTML & CSS…",
    "Matching your brand theme…",
    "Polishing the details…",
  ],
  abtest: [
    "Analyzing your current page…",
    "Extracting your brand colors and fonts…",
    "Collecting your real images…",
    "Reviewing heatmap & user-journey insights…",
    "Designing the variant…",
    "Writing responsive HTML & CSS…",
    "Matching your brand theme…",
    "Polishing the details…",
  ],
};

// Full-page AI A/B Test Studio — chat on the left, live UI preview on the right
// (Claude-artifacts style). Reached from the AI chat's quick question.
const AbTestStudio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sessionUser = useSelector(selectLogInUsername);
  const savedArtifact = useSelector(selectStudioArtifact);
  const clientId = sessionUser ? sessionUser.split("@")[0] : null;

  // Studio mode: "abtest" (test one section) or "landing" (rebuild the whole page).
  const mode = location.state?.mode === "landing" ? "landing" : "abtest";
  const isLanding = mode === "landing";
  const studioTitle = isLanding ? "Landing Page Studio" : "A/B Test Studio";
  const studioSubtitle = isLanding
    ? "AI rebuilds your landing page from your analytics"
    : "AI builds a variant you can preview & test";

  const [messages, setMessages] = useState([]);
  const [artifact, setArtifact] = useState(null); // { html, page, section, hypothesis, condition, variantName }
  const [inputVal, setInputVal] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [view, setView] = useState("preview"); // 'preview' | 'code'
  const [device, setDevice] = useState("desktop"); // 'desktop' | 'mobile'
  const [copied, setCopied] = useState(false);
  const [statusText, setStatusText] = useState("");
  const startedRef = useRef(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isGenerating, statusText]);

  // Cycle the "running text" while generating so the user sees live progress.
  useEffect(() => {
    if (!isGenerating) {
      setStatusText("");
      return undefined;
    }
    const steps = STATUS_STEPS[isLanding ? "landing" : "abtest"];
    let i = 0;
    setStatusText(steps[0]);
    const id = setInterval(() => {
      i = (i + 1) % steps.length;
      setStatusText(steps[i]);
    }, 2500);
    return () => clearInterval(id);
  }, [isGenerating, isLanding]);

  const generate = async (text, isFirst, pageUrl) => {
    if (!clientId || !text || isGenerating) return;
    // Build the conversation explicitly (state updaters run async, so we can't
    // read the result back reliably). baseMsgs = existing chat + this user turn.
    const userMsg = { id: `u_${Date.now()}`, sender: "user", text };
    const baseMsgs = [...messages, userMsg];
    setMessages(baseMsgs);
    setIsGenerating(true);
    try {
      const payload = isFirst
        ? { request: text, ...(pageUrl ? { pageUrl } : {}) }
        : { instruction: text, currentHtml: artifact?.html || "" };
      const callApi = () =>
        isLanding
          ? agentAPI.landingBuilder(clientId, payload)
          : agentAPI.abtestStudio(clientId, payload);
      // Generation is long (30s-3min); a dropped connection (proxy timeout, wifi
      // blip) surfaces as a network error with no response — retry once.
      let res;
      try {
        res = await callApi();
      } catch (e) {
        if (!e.response) {
          await new Promise((r) => setTimeout(r, 2000));
          res = await callApi();
        } else {
          throw e;
        }
      }
      const d = res.data || {};
      const summary = [
        d.explanation || d.description,
        d.page && `\n\n- **Page:** ${d.page}`,
        d.section && `\n- **Section:** ${d.section}`,
        d.controlSummary && `\n- **A (Control):** ${d.controlSummary}`,
        d.variantSummary && `\n- **B (Variant):** ${d.variantSummary}`,
        !d.controlSummary && d.description && `\n- **Change:** ${d.description}`,
        d.why && `\n- **Why:** ${d.why}`,
        d.trafficSplit && `\n- **Traffic split:** ${d.trafficSplit}`,
        d.expectedResult && `\n- **Expected result:** ${d.expectedResult}`,
      ]
        .filter(Boolean)
        .join("");
      const agentMsg = { id: `a_${Date.now()}`, sender: "agent", text: summary || "Here's your variant." };
      const finalMsgs = [...baseMsgs, agentMsg];
      setMessages(finalMsgs);
      if (d.html) {
        // Carry the whole conversation with the artifact so the drawer can show
        // the full chat next to the code file when minimized.
        const withId = { ...d, artifactId: `art_${Date.now()}`, conversation: finalMsgs };
        setArtifact(withId);
        setView("preview");
        dispatch(setStudioArtifact(withId));
      }
    } catch (err) {
      const isNetwork = !err.response;
      const msg = isNetwork
        ? "The design is taking longer than the connection allowed and it timed out. Please try again — if it keeps happening, the server request timeout needs to be raised."
        : `Couldn't generate the variant. ${err?.response?.data?.error || err.message || ""}`;
      setMessages([
        ...baseMsgs,
        { id: `e_${Date.now()}`, sender: "agent", text: msg, isError: true },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  // Fetch the pages worth working on, then let the user pick one.
  const fetchCandidates = async () => {
    setIsGenerating(true);
    try {
      const res = await agentAPI.landingCandidates(clientId);
      const candidates = res.data?.candidates || [];
      const prompt = isLanding
        ? "Here are the pages that could benefit most from a redesign. Pick one and I will rebuild it:"
        : "Here are the pages that would benefit most from an A/B test. Pick one and I will design a test for its most important section:";
      setMessages((p) => [
        ...p,
        { id: `cand_${Date.now()}`, sender: "agent", text: prompt, candidates },
      ]);
    } catch (err) {
      setMessages((p) => [
        ...p,
        { id: `e_${Date.now()}`, sender: "agent", text: `Couldn't load page suggestions. ${err?.response?.data?.error || err.message || ""}`, isError: true },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePick = (c) => {
    if (isGenerating) return;
    const req = isLanding
      ? `Redesign the ${c.page} page to improve conversion rate`
      : `Create an A/B test for the most important section on ${c.page}`;
    generate(req, true, c.url || undefined);
  };

  // On mount: landing -> suggest pages to pick; abtest -> generate a variant;
  // or restore the last artifact (when reopened via "Open in studio").
  useEffect(() => {
    if (startedRef.current) return;
    const initial = location.state?.initialRequest;
    if (clientId && initial) {
      startedRef.current = true;
      // Both modes start by suggesting the pages worth working on.
      setMessages([{ id: `u_${Date.now()}`, sender: "user", text: initial }]);
      fetchCandidates();
    } else if (savedArtifact?.html) {
      startedRef.current = true;
      setArtifact(savedArtifact);
      setView("preview");
      setMessages(
        Array.isArray(savedArtifact.conversation) && savedArtifact.conversation.length
          ? savedArtifact.conversation
          : [{ id: `restored_${savedArtifact.artifactId || "x"}`, sender: "agent", text: savedArtifact.description || savedArtifact.explanation || "Here is your generated UI. You can refine it below." }]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, location.state, savedArtifact]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const t = inputVal.trim();
    if (!t || isGenerating) return;
    setInputVal("");
    generate(t, !artifact);
  };

  const handleCopy = async () => {
    if (!artifact?.html) return;
    try {
      await navigator.clipboard.writeText(artifact.html);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="flex-grow flex flex-col h-[calc(100vh-90px)] min-h-[560px] p-3 gap-3">
      {/* Header */}
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          title="Back"
        >
          <FaArrowLeft />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center text-white">
            <FaFlask />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-800">{studioTitle}</h2>
            <p className="text-[11px] text-slate-500">{studioSubtitle}</p>
          </div>
        </div>
        {artifact?.html && (
          <button
            onClick={() => navigate(-1)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
            title="Minimize — keep the file available in the AI drawer"
          >
            <FaWindowMinimize className="text-[10px]" /> Minimize
          </button>
        )}
      </div>

      {/* Split layout */}
      <div className="flex-grow flex flex-col lg:flex-row gap-3 min-h-0">
        {/* LEFT: chat */}
        <div className="lg:w-[38%] flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden min-h-[280px]">
          <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Chat
          </div>
          <div className="flex-grow overflow-y-auto p-4 space-y-3 bg-slate-50/50">
            {messages.length === 0 && !isGenerating && (
              <p className="text-xs text-slate-400">
                {isLanding
                  ? "Describe the landing page you want — e.g. “Rebuild my product landing page to convert better”."
                  : "Describe the A/B test you want — e.g. “Improve the product page hero CTA”."}
              </p>
            )}
            {messages.map((m) => {
              const isAgent = m.sender === "agent";
              return (
                <div key={m.id} className={`flex items-start gap-2 ${isAgent ? "" : "flex-row-reverse"}`}>
                  <div
                    className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs ${
                      isAgent ? "bg-cyan-100 text-cyan-700" : "bg-slate-800 text-white"
                    }`}
                  >
                    {isAgent ? <FaRobot /> : <FaUser />}
                  </div>
                  <div className={`flex flex-col ${isAgent ? "items-start" : "items-end"} max-w-[85%]`}>
                    <div
                      className={`p-3 rounded-2xl text-xs leading-relaxed shadow-sm ${
                        isAgent
                          ? `bg-white border border-slate-200 text-slate-800 rounded-tl-none ${m.isError ? "text-rose-600" : ""}`
                          : "bg-slate-900 text-white rounded-tr-none whitespace-pre-wrap"
                      }`}
                    >
                      {isAgent ? <ChatMarkdown text={safeText(m.text)} /> : safeText(m.text)}
                    </div>
                    {/* Page candidates to pick from (landing mode) */}
                    {isAgent && Array.isArray(m.candidates) && m.candidates.length > 0 && (
                      <div className="mt-2 w-full space-y-1.5">
                        {m.candidates.map((c, i) => (
                          <button
                            key={i}
                            type="button"
                            disabled={isGenerating}
                            onClick={() => handlePick(c)}
                            className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors disabled:opacity-50"
                          >
                            <span className="block text-xs font-semibold text-slate-800">{c.page}</span>
                            <span className="block text-[11px] text-slate-500">{c.reason}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {isGenerating && (
              <div className="flex items-center gap-2 text-xs text-cyan-700 font-medium">
                <FaSpinner className="animate-spin" />
                <span className="animate-pulse">{statusText || "Working…"}</span>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={handleSubmit} className="p-3 border-t border-slate-100 flex items-center gap-2">
            <input
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              placeholder={
                artifact
                  ? isLanding ? "Refine the landing page…" : "Refine the variant…"
                  : isLanding ? "Describe your landing page…" : "Describe your A/B test…"
              }
              disabled={isGenerating}
              className="flex-grow px-3 py-2 bg-slate-100 rounded-xl text-xs outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={!inputVal.trim() || isGenerating}
              className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold disabled:opacity-40 flex items-center gap-1.5"
            >
              <FaPaperPlane className="text-[10px]" /> Send
            </button>
          </form>
        </div>

        {/* RIGHT: artifact */}
        <div className="lg:w-[62%] flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden min-h-[320px]">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-700 truncate">
              {artifact?.variantName || "Preview"}
            </span>
            <div className="flex items-center gap-2">
              {/* Preview / Code toggle */}
              <div className="flex rounded-lg bg-slate-100 p-0.5">
                <button
                  onClick={() => setView("preview")}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    view === "preview" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"
                  }`}
                >
                  <FaEye className="text-[10px]" /> Preview
                </button>
                <button
                  onClick={() => setView("code")}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    view === "code" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"
                  }`}
                >
                  <FaCode className="text-[10px]" /> Code
                </button>
              </div>
              <button
                onClick={handleCopy}
                disabled={!artifact?.html}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                title="Copy code"
              >
                {copied ? <FaCheck className="text-[10px] text-emerald-500" /> : <FaCopy className="text-[10px]" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <button
                onClick={() => downloadHtml(artifact?.html, artifact?.variantName)}
                disabled={!artifact?.html}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                title="Download .html"
              >
                <FaDownload className="text-[10px]" /> Download
              </button>
              {/* Desktop / mobile preview toggle */}
              {view === "preview" && (
                <div className="flex rounded-lg bg-slate-100 p-0.5">
                  <button
                    onClick={() => setDevice("desktop")}
                    className={`flex items-center px-2 py-1 rounded-md text-[11px] transition-colors ${device === "desktop" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
                    title="Desktop view"
                  >
                    <FaDesktop className="text-[11px]" />
                  </button>
                  <button
                    onClick={() => setDevice("mobile")}
                    className={`flex items-center px-2 py-1 rounded-md text-[11px] transition-colors ${device === "mobile" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
                    title="Mobile view"
                  >
                    <FaMobileAlt className="text-[11px]" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex-grow min-h-0 bg-slate-50">
            {!artifact?.html ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 p-6">
                {isGenerating ? (
                  <>
                    <FaSpinner className="text-3xl mb-3 text-cyan-500 animate-spin" />
                    <p className="text-sm font-semibold text-cyan-700 animate-pulse">{statusText || "Working…"}</p>
                    <p className="text-[11px] text-slate-400 mt-1">Building an on-brand design from your real page…</p>
                  </>
                ) : (
                  <>
                    <FaFlask className="text-3xl mb-2" />
                    <p className="text-xs max-w-[260px]">
                      {isLanding
                        ? "Your generated landing page will appear here."
                        : "Your generated A/B test variant will appear here."}
                    </p>
                  </>
                )}
              </div>
            ) : view === "preview" ? (
              device === "mobile" ? (
                <div className="w-full h-full flex items-start justify-center overflow-auto py-4 bg-slate-100">
                  <iframe
                    title="variant-preview-mobile"
                    sandbox="allow-scripts allow-forms"
                    srcDoc={artifact.html}
                    style={{ width: 390, height: 780 }}
                    className="border border-slate-300 rounded-[24px] shadow-lg bg-white shrink-0"
                  />
                </div>
              ) : (
                <iframe
                  title="variant-preview"
                  sandbox="allow-scripts allow-forms"
                  srcDoc={artifact.html}
                  className="w-full h-full border-0 bg-white"
                />
              )
            ) : (
              <pre className="w-full h-full overflow-auto p-4 text-[11px] leading-relaxed text-slate-800 bg-slate-900/95 text-slate-100">
                <code>{artifact.html}</code>
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbTestStudio;
